import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Users } from "lucide-react";
import { toast } from "sonner";
import { Field, Sel, inputCls } from "../components/Form";
import { errMsg, useApi, loadFailed } from "../lib/api";
import { useMe } from "../lib/me";

const StartLesson = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useMe();
  const [people, setPeople] = useState([]);
  const [courses, setCourses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [f, setF] = useState({ title: "", lesson_type: "group", access: "invited", course_id: "", lesson_plan_id: "", invited_user_ids: [] });
  useEffect(() => {
    api.get("/users/directory").then((r) => setPeople(r.data)).catch(loadFailed("students"));
    api.get("/courses").then((r) => setCourses(r.data.filter((c) => me.role === "admin" || c.teacher_id === me.user_id))).catch(loadFailed("courses"));
    api.get("/lesson-plans").then((r) => setPlans(r.data)).catch(loadFailed("lesson plans"));
  }, [api, me]);
  const max = f.lesson_type === "one_on_one" ? 1 : 10;
  const toggle = (id) => setF((x) => {
    const has = x.invited_user_ids.includes(id);
    if (!has && x.invited_user_ids.length >= max) { toast.error(`Up to ${max} student${max > 1 ? "s" : ""}`); return x; }
    return { ...x, invited_user_ids: has ? x.invited_user_ids.filter((i) => i !== id) : [...x.invited_user_ids, id] };
  });
  const start = async () => {
    if (!f.title) return toast.error("Name the lesson");
    try {
      const { data } = await api.post("/lessons", { ...f, course_id: f.course_id || null, lesson_plan_id: f.lesson_plan_id || null });
      navigate(`/lesson/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="panel p-6 space-y-4" data-testid="start-lesson-form">
      <h2 className="text-lg font-bold">Start a lesson session</h2>
      <Field label="Lesson title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="lesson-title-input" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Type"><Sel value={f.lesson_type} onChange={(v) => setF({ ...f, lesson_type: v, invited_user_ids: v === "one_on_one" ? f.invited_user_ids.slice(0, 1) : f.invited_user_ids })} testId="lesson-type-select"
          options={[{ value: "group", label: "Small group (up to 10)" }, { value: "one_on_one", label: "One-on-one" }]} /></Field>
        <Field label="Who can join"><Sel value={f.access} onChange={(v) => setF({ ...f, access: v })} testId="lesson-access-select"
          options={[{ value: "invited", label: "Only students I invite" }, { value: "course", label: "Students in a course" }, { value: "members", label: "Any camp member" }]} /></Field>
      </div>
      {f.access === "course" && <Field label="Course"><Sel value={f.course_id} onChange={(v) => setF({ ...f, course_id: v })} testId="lesson-course-select" placeholder="Pick a course" options={courses.map((c) => ({ value: c.id, label: c.title }))} /></Field>}
      <Field label="Lesson plan"><Sel value={f.lesson_plan_id} onChange={(v) => setF({ ...f, lesson_plan_id: v })} testId="lesson-plan-select" placeholder="No plan" options={plans.map((p) => ({ value: p.id, label: p.title }))} /></Field>
      <Field label={`Invite students (${f.invited_user_ids.length}/${max})`}>
        <div className="max-h-48 overflow-y-auto rounded-[3px] border border-zinc-800 divide-y divide-zinc-900">
          {people.length === 0 && <p className="p-3 text-sm text-zinc-500">No other users have signed up yet.</p>}
          {people.map((p) => (
            <label key={p.user_id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-white/[.03]">
              <input type="checkbox" className="accent-yellow-400" checked={f.invited_user_ids.includes(p.user_id)} onChange={() => toggle(p.user_id)} data-testid={`invite-student-${p.user_id}`} />
              <span className="flex-1 truncate">{p.name}</span><span className="mono text-[10px] text-zinc-500">{p.email}</span>
            </label>
          ))}
        </div>
      </Field>
      <button onClick={start} data-testid="start-lesson-btn" className="w-full rounded-[3px] bg-[#39ff6a] py-2.5 font-bold text-black hover:brightness-110">Open the Lesson Room</button>
    </section>
  );
};

export default function LessonHub() {
  const api = useApi();
  const { isStaff } = useMe();
  const [lessons, setLessons] = useState(null);
  useEffect(() => { api.get("/lessons").then((r) => setLessons(r.data)).catch(() => setLessons([])); }, [api]);
  return (
    <div className="spotlight min-h-screen" data-testid="lesson-hub-page">
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-10">
        <span className="mono-label text-[#39ff6a]">Studio 09 · Private</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight flex items-center gap-3"><GraduationCap className="h-10 w-10 text-[#39ff6a]" /> Virtual Lesson Room</h1>
        <p className="mt-3 text-zinc-400 max-w-2xl">Face-to-face lessons. Everyone can talk and play; only invited or enrolled students can join each session.</p>
        <div className={`mt-10 grid gap-8 ${isStaff ? "lg:grid-cols-[1fr_440px]" : ""}`}>
          <section className="space-y-3" data-testid="open-lessons">
            <h2 className="text-lg font-bold">Open lessons for you</h2>
            {lessons?.length === 0 && <p className="text-sm text-zinc-500 panel p-5" data-testid="no-lessons">No lesson sessions are open for you right now.</p>}
            {lessons?.map((l) => (
              <Link key={l.id} to={`/lesson/${l.id}`} className="door panel p-5 flex items-center gap-4" data-testid={`lesson-card-${l.id}`}>
                <span className="h-2 w-2 rounded-full bg-[#39ff6a] glow-green" />
                <div className="flex-1 min-w-0"><p className="font-bold truncate">{l.title}</p>
                  <p className="mono text-[11px] text-zinc-400">{l.teacher_name} · {l.lesson_type === "one_on_one" ? "One-on-one" : "Group"} · <Users className="inline h-3 w-3" /> {l.invited_user_ids.length} invited</p></div>
                <span className="rounded-[3px] bg-[#39ff6a] px-3 py-1.5 text-sm font-bold text-black">Join lesson</span>
              </Link>
            ))}
          </section>
          {isStaff && <StartLesson />}
        </div>
      </div>
    </div>
  );
}
